import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import getRandomId from '@/utils/uuid';
import { OccType, MemberLevel } from '@/config/constant';
import { isNil, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import ReserveService from '@/api/reserve';

import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';
import {
  createDefaultRentTime,
  findItemByKey,
  flatPriceList,
  getDayDepartureTime,
  getEmptyCouponData,
} from '@/redux/actions/reservation/helper';
import { DetectMethod, MainState, ReducerState } from '@/redux/reducers/reservation/createReserve';
import {
  RentRoomInfo,
  RentTimeInfo,
  RoomPriceInfo,
  SelectRoomItem,
} from '@/redux/reducers/reservation/base.d';
import { parseAreaMobileStr } from '@/components/BaseMobileInput/helper';

// 默认数据
interface DefaultStateConfig {
  selectRoom?: Array<{ indexId: string; roomId: number; roomTypeId: number }>;
  arrivalTime?: number;
}
export const getDefaultState: (config?: DefaultStateConfig) => MainState = (config) => {
  const { selectRoom, arrivalTime } = config || {};
  const { dayTypeTimeInfo, hourTypeTimeInfo } = createDefaultRentTime(arrivalTime);
  return {
    commonInfo: {
      memberLevel: MemberLevel.Common,
      occType: OccType.DayUse,
    },
    customerInfo: {
      name: '',
      vipDetectMethod: DetectMethod.Auto,
      mobile: '',
      otaOrderNo: '',
      email: '',
    },
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    channelInfo: {
      otaPaymentAmount: '',
      invoiceAmount: '',
    },
    remarkInfo: {
      remark: '',
      checkInRemark: '',
    },
    defaultSelectRoom: selectRoom || [],
  };
};

// 追加预订 原单数据
export const getStateFromMain: (
  orderId: string,
  config?: DefaultStateConfig,
) => Promise<MainState> = async (orderId, config) => {
  const defaultState = getDefaultState(config);
  const { commonInfo, dayTypeTimeInfo } = defaultState;
  const res = await ReserveService.getReserveDetail(orderId);
  const orderInfo = res.data || {};
  const { memberLevel } = orderInfo || {};
  if (!isNil(memberLevel) && memberLevel !== MemberLevel.Common) {
    // 会员 离店时间14:00
    dayTypeTimeInfo.expectedDepartureTime = getDayDepartureTime(dayTypeTimeInfo, '14:00:00');
  }
  return {
    ...defaultState,
    commonInfo: {
      ...commonInfo,
      appendOrderId: orderId,
      memberLevel,
    },
    customerInfo: {
      name: orderInfo.name,
      vipDetectMethod: DetectMethod.Auto,
      mobile: orderInfo.mobile,
      otaOrderNo: orderInfo.otaOrderNo,
      wyuId: orderInfo.wyuId,
      email: orderInfo.email,
    },
  };
};

// 暂存详情
export const getStateFromTemp: (
  tempId: string,
) => Promise<{ state: MainState; message: string }> = async (tempId) => {
  const res = await ReserveService.getStashDetail(tempId);
  const data = res.data || {};
  const occType = data.occType === OccType.DayUse ? OccType.DayUse : OccType.TimeRENT;
  const { dayTypeTimeInfo, hourTypeTimeInfo } = createDefaultRentTime();
  const selectRoomList: SelectRoomItem[] = [];
  const rentRoomInfos: RentRoomInfo[] = [];
  const initList = data.subOrderCreateRespList || [];
  const emptyCoupon = getEmptyCouponData();
  const rentDateIdMap = {}; // 时间idmap
  initList.forEach((room) => {
    const { roomTypeId, roomTypeName, roomId, roomNo } = room;
    const roomInfo = { roomTypeId, roomTypeName, roomId, roomNo };
    const roomPriceList: RoomPriceInfo[] = [];
    (room.subOrderRoomPriceCreateRespList || []).forEach((dayPrice) => {
      const { rentDate } = dayPrice;
      let rentDateId = rentDateIdMap[rentDate];
      if (!rentDateId) {
        rentDateId = getRandomId();
        rentDateIdMap[rentDate] = rentDateId;
      }
      roomPriceList.push({
        ...emptyCoupon,
        ...dayPrice,
        couponValue: parseFen2Yuan(dayPrice.couponAmount),
        rentDateId,
        rentDate: format2DateStr(rentDate),
        occType,
        inputPrice: parseFen2Yuan(dayPrice.price),
      });
    });
    const itemIndex = getRandomId();
    selectRoomList.push({
      indexId: itemIndex,
      roomTypeId,
      roomId,
    });
    rentRoomInfos.push({ ...roomInfo, indexId: itemIndex, roomPriceList });
  });
  const result: Partial<ReducerState> = {
    commonInfo: {
      occType,
      tempOrderId: tempId,
      memberLevel: data.memberLevel,
    },
    customerInfo: {
      name: data.name,
      vipDetectMethod: data.autoMemberFlag ? DetectMethod.Auto : DetectMethod.Manual,
      mobile: data.mobile,
      otaOrderNo: data.otaOrderNo,
      wyuId: data.wyuId,
      email: data.email,
    },
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    channelInfo: {
      channelId: data.sourceChannelId,
      strategyId: data.priceStrategyId,
      channelOrderNo: data.channelOrderNo,
      otaPaymentAmount: parseFen2Yuan(data.otaPaymentAmount),
      invoiceAmount: '',
    },
    remarkInfo: {
      remark: data.remark,
      checkInRemark: data.checkinRemark,
    },
    selectRoomList,
    rentRoomInfos,
    defaultSelectRoom: [],
  };
  const defaultTypeTimeInfo: RentTimeInfo = {
    occType,
    rentTime: data.occDuration,
    expectedArrivalTime: data.expectedArrivalTime ? DateJS(data.expectedArrivalTime) : null,
    expectedDepartureTime: data.expectedDepartureTime ? DateJS(data.expectedDepartureTime) : null,
  };
  if (occType === OccType.DayUse) {
    result.dayTypeTimeInfo = defaultTypeTimeInfo;
  }
  if (occType === OccType.TimeRENT) {
    result.hourTypeTimeInfo = defaultTypeTimeInfo;
  }
  return { state: result as MainState, message: data.message };
};

// 提交数据
export const formatToSubmitData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const {
    commonInfo,
    customerInfo,
    channelInfo,
    dayTypeTimeInfo,
    hourTypeTimeInfo,
    rentRoomInfos,
    remarkInfo,
    channelList,
    strategyList,
    roomPriceList,
  } = data;
  const rentTimeInfo = commonInfo.occType === OccType.DayUse ? dayTypeTimeInfo : hourTypeTimeInfo;
  const { channelId, strategyId, channelOrderNo, otaPaymentAmount, invoiceAmount } = channelInfo;
  const sourceChannelName = findItemByKey(channelList, 'id', channelId)?.name;
  const priceStrategyName = findItemByKey(strategyList, 'id', strategyId)?.showName;
  const { cachePriceMap } = flatPriceList(roomPriceList);
  const { mobileStr } = parseAreaMobileStr(customerInfo.mobile);

  return {
    autoMemberFlag: customerInfo.vipDetectMethod === DetectMethod.Auto,
    wyuId: customerInfo.wyuId,
    channelOrderNo,
    expectedArrivalTime: format2TimeStamp(rentTimeInfo.expectedArrivalTime),
    expectedDepartureTime: format2TimeStamp(rentTimeInfo.expectedDepartureTime),
    memberFlag: true,
    memberLevel: commonInfo.memberLevel,
    mobile: mobileStr ? customerInfo.mobile : null,
    name: customerInfo.name,
    email: customerInfo.email,
    occDuration: rentTimeInfo.rentTime,
    occType: rentTimeInfo.occType,
    otaOrderNo: customerInfo.otaOrderNo,
    otaPaymentAmount: parseYuan2Fen(otaPaymentAmount),
    invoiceAmount: parseYuan2Fen(invoiceAmount),
    priceStrategyId: strategyId,
    priceStrategyName,
    remark: remarkInfo.remark,
    checkinRemark: remarkInfo.checkInRemark,
    resvType: 0,
    sourceChannelId: channelId,
    sourceChannelName,
    subOrderCreateVOS: rentRoomInfos.map((room) => {
      const { roomId, roomNo, roomTypeId, roomTypeName, roomPriceList: itemPriceList } = room;
      const strategyPriceMap = cachePriceMap[roomTypeId] || {};
      return {
        roomId, // 待排房 没有房号
        roomNo,
        roomTypeId,
        roomTypeName,
        sourceChannelId: channelId,
        sourceChannelName,
        subOrderRoomPriceCreateVOS: itemPriceList.map((dayInfo) => {
          const {
            rentDate,
            inputPrice,
            couponAmount,
            couponName,
            couponNo,
            couponType,
            couponWyuid,
            couponSubType,
          } = dayInfo;
          return {
            rentDate: format2TimeStamp(rentDate),
            price: parseYuan2Fen(inputPrice),
            priceStrategyId: strategyId,
            priceStrategyName,
            strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
            couponAmount,
            couponName,
            couponNo,
            couponType,
            couponWyuid,
            couponSubType,
          };
        }),
      };
    }),
  };
};

// 暂存提交数据
export const formatToTempData: (data: ReducerState) => { [key: string]: unknown } = (data) => {
  const { dayTypeTimeInfo, hourTypeTimeInfo } = data;
  const submitData = formatToSubmitData(data);
  const rentTimeInfo = submitData.occType === OccType.DayUse ? dayTypeTimeInfo : hourTypeTimeInfo;
  if (!rentTimeInfo.expectedArrivalTime) {
    // 预抵时间未空时 不需要提交
    delete submitData.expectedArrivalTime;
  }
  return submitData;
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.Reserve);
